import { Files } from "@phosphor-icons/react";
import { useState } from "react";

import type { Rb2bIntegration as Rb2bIntegrationType } from "features/integrations/types";

import CopiableInput from "common/components/CopiableInput";
import { Button } from "common/components/ui/Button";
import FilterButton from "common/components/ui/FilterButton";
import FormField from "common/components/ui/FormField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "common/components/ui/Select";
import useCampaigns from "common/datahooks/useCampaigns";

import useIntegrationMutations from "../datahooks/useIntegrationMutations";
import RemoveIntegrationButton from "./RemoveIntegrationButton";
import SettingsContainer from "./SettingsContainer";

interface Rb2bIntegrationProps {
  settings: Rb2bIntegrationType | null;
}

export default function Rb2bIntegrationSettings({
  settings,
}: Rb2bIntegrationProps) {
  const isConnected = settings !== null;

  const [selectedCampaignId, setSelectedCampaignId] = useState(
    settings?.campaign_id,
  );
  const { campaigns, isLoadingCampaigns } = useCampaigns();
  const { connectRb2b, isConnectingRb2b } = useIntegrationMutations();

  let selectedCampaignLabel = "Select campaign";

  if (selectedCampaignId) {
    const campaign = campaigns?.find(({ id }) => id === selectedCampaignId);

    selectedCampaignLabel = campaign?.name ?? "Deleted campaign";
  }

  const generateButtonComponent = (
    <Button
      variant="outline-black"
      disabled={isConnected || !selectedCampaignId}
      isLoading={isConnectingRb2b}
      onClick={() => connectRb2b(selectedCampaignId)}
    >
      Generate
    </Button>
  );

  return (
    <SettingsContainer integrationKey="rb2b">
      <div className="flex flex-col lg:p-4 gap-6">
        <FormField label="Select campaign" htmlFor="campaign-select">
          <Select
            value={selectedCampaignId}
            onValueChange={setSelectedCampaignId}
            disabled={isConnected}
          >
            <SelectTrigger asChild>
              <FilterButton leftIcon={<Files />} isLoading={isLoadingCampaigns}>
                {selectedCampaignLabel}
              </FilterButton>
            </SelectTrigger>
            <SelectContent className="max-w-[--radix-select-trigger-width]">
              {(campaigns || []).map((campaign) => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        {isConnected ? (
          <FormField
            label="Copy this URL and paste it to your RB2B settings"
            htmlFor="webhook-url"
            hint="Generating a new RB2B URL will invalidate any previously generated URLS"
          >
            <CopiableInput id="webhook-url" value={settings?.webhook_url} />
          </FormField>
        ) : (
          generateButtonComponent
        )}
      </div>

      {isConnected && (
        <div className="flex flex-col gap-y-6 lg:p-4">
          <RemoveIntegrationButton integrationKey="rb2b" />
        </div>
      )}
    </SettingsContainer>
  );
}
